import { apiClient } from "@/lib/http";
import { create } from "zustand";
import type { User } from "./User";
import { deleteToken, setToken } from "@/lib/token";
import { type Business } from "./Business";
import { type Shop } from "./Shop";
import type { Employee } from "./Employee";

export interface Auth {
  user: User;
}

interface AuthSession {
  user: User;
  currentShop: Shop;
  business: Business;
  message: string;
  employee: Employee;
  shop: Shop;
  isAuthenticated: boolean;
}

interface RegisterData {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  password: string;
}

interface ResetPasswordData {
  token: string;
  password: string;
}

interface AuthState {
  user: User | undefined;
  session: AuthSession | undefined;

  setUser: (user: User | undefined) => void;
  setSession: (session: AuthSession) => void;

  login: (email: string, password: string) => Promise<any>;
  register: (data: RegisterData) => Promise<any>;
  fetchSession: () => Promise<any>;
  logout: () => void;
  passwordForgot: (email: string) => Promise<any>;
  resetPassword: (data: ResetPasswordData) => Promise<any>;
  sendVerificationEmail: () => Promise<any>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: undefined,
  session: undefined,

  setUser: (user: User | undefined) => {
    set(() => ({ user }));
  },

  setSession: (session: AuthSession) => {
    set((state) => ({
      session: {
        ...state.session,
        ...session,
      },
    }));
  },

  register: (data: RegisterData) => {
    return apiClient()
      .post("/auth/register", data)
      .then((res) => {
        const session = res.data;
        get().setSession(session);
        get().setUser(session.user);
        setToken(session.token);
        return Promise.resolve(res.data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  login: (email: string, password: string) => {
    return apiClient()
      .post("/auth/login", { email, password })
      .then((res) => {
        const session = res.data;
        get().setSession(session);
        get().setUser(session.user);
        setToken(session.token);
        return Promise.resolve(session);
      });
  },

  fetchSession: () => {
    return apiClient()
      .post("/auth/check")
      .then((res) => {
        const session = res.data;

        if (!session.isAuthenticated) {
          deleteToken();
          window.location.assign("/auth/login");
        }

        if (session.user.email_verified_at == null) {
          if (window.location.pathname != "/auth/verify-account") {
            window.location.assign("/auth/verify-account");
            return;
          }
        } else {
          if (window.location.pathname == "/auth/verify-account") {
            window.location.assign("/");
          }
        }

        set(() => {
          return {
            user: session.user,
            session: {
              ...session,
              currentShop: session.shop,
            },
          };
        });

        if (session.shop)
          localStorage.setItem("locale", session?.shop?.language);

        return Promise.resolve(session);
      })
      .catch((error) => {
        deleteToken();
        window.location.assign("/auth/login");
        return Promise.reject(error);
      });
  },
  logout: () => {
    deleteToken();
    window.location.assign("/");
  },
  passwordForgot: (email: string) => {
    return apiClient().post("/auth/password-forgot", { email });
  },
  resetPassword: (data: ResetPasswordData) => {
    return apiClient().post("/auth/reset-password", data);
  },
  sendVerificationEmail: () => {
    return apiClient().post("/auth/send-verify", {
      email: get().session?.user?.email,
    });
  },
}));
